/**
 * @fileoverview Report Upload
 * @description File picker and drop target for Scout consolidated HTML reports.
 * @module features/ScoutEval/components/ReportUpload
 */

import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { FileUp } from 'lucide-react';

/**
 * Render the report upload control, optionally wrapping a drop area.
 * @param {Object} props - Upload handler, busy flag, label, variant, button visibility, and children.
 * @returns {JSX.Element} Upload control.
 */
export function ReportUpload({ onFile, busy, label = 'Upload report', variant = 'contained', showButton = true, children }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  /**
   * Hand the first selected or dropped file to the caller.
   * @param {FileList|null} files - Selected files.
   * @returns {void}
   */
  const handleFiles = (files) => {
    const file = files?.[0];
    if (file) {
      onFile(file);
    }
  };

  return (
    <Box
      className={dragging ? 'scout-dropzone scout-dropzone--active' : 'scout-dropzone'}
      onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        handleFiles(event.dataTransfer?.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".html,.htm,text/html"
        hidden
        data-testid="scout-report-input"
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <Button
        variant={variant}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={15} color="inherit" /> : <FileUp size={16} />}
        onClick={() => inputRef.current?.click()}
        sx={showButton ? undefined : { display: 'none' }}
      >
        {busy ? 'Parsing…' : label}
      </Button>
      {children}
    </Box>
  );
}

export default ReportUpload;
